import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Checkbox, Dropdown, Form, Grid, Segment } from "semantic-ui-react";
import { Config } from "../../../../config/api";
import { useDebounce, useForm } from "../../../../libs/component_utils";
import HttpConnect from "../../../../libs/http_connect";
import AgenciesService from "../../../../services/agencies";
import PixelsService from "../../../../services/pixels";
import T1Service from "../../../../services/t1";
import "./index.css";
import { default1PSeller } from "../../../../libs/consts";

const ProductCatalogPermissionsForm = ({
  formType = "create",
  initialData,
  isSubmitting,
  onCancel,
  submitText = "Save Changes",
  onSubmit,
}) => {
  const services = useRef(
    new Map([
      ["agencies", new AgenciesService()],
      ["pixels", new PixelsService()],
      ["t1", new T1Service()],
    ]),
  );
  const intl = useIntl();
  const allAdvertisersOption = useMemo(
    () => ({
      key: "all-advertisers",
      text: intl.formatMessage({
        id: "LABEL_ALL_ADVERTISERS",
        defaultMessage: "All Advertisers",
      }),
      value: "all-advertisers",
    }),
    [intl],
  );

  const allBrandsOption = useMemo(
    () => ({
      key: "all-brands",
      text: intl.formatMessage({
        id: "LABEL_ALL_BRANDS",
        defaultMessage: "All Brands",
      }),
      value: "all-brands",
    }),
    [intl],
  );
  const isEditMode = formType.toString() === "edit";
  const [agenciesData, setAgenciesData] = useState({
    agencies: [],
    agenciesLoading: false,
  });
  const [advertisersData, setAdvertisersData] = useState({
    advertisers: [allAdvertisersOption],
    advertisersLoading: false,
  });
  const [sellersData, setSellersData] = useState({
    sellers: [
      {
        key: "hint",
        text: intl.formatMessage({
          id: "PLACEHOLDER_START_TYPING_3P",
          defaultMessage: "Start typing to search for 3P sellers...",
        }),
        value: "hint",
        disabled: true,
      },
      default1PSeller,
    ],
    sellersLoading: false,
  });
  const [brandsData, setBrandsData] = useState({
    brands: [
      {
        key: "hint",
        text: intl.formatMessage({
          id: "PLACEHOLDER_START_TYPING",
          defaultMessage: "Start typing to search...",
        }),
        value: "hint",
        disabled: true,
      },
      allBrandsOption,
    ],
    brandsLoading: false,
  });
  const [disabledFields, setDisabledFields] = useState([]);
  const checkIfAdminAgency = () => {
    return (
      isEditMode &&
      initialData.agency_id &&
      initialData.advertiser_ids.length === 0 &&
      initialData.seller_ids.length === 0 &&
      initialData.brands.length === 0
    );
  };
  const [isAdminAgency, setIsAdminAgency] = useState(() => checkIfAdminAgency());

  const initialAgencies = useRef(null);
  const prevAgency = useRef(null);
  const sellerQuery = useRef("");
  const brandQuery = useRef("");

  const refineValues = useCallback(
    (values) => {
      const newValues = { ...values };
      if (newValues.advertiser_ids.length === 1 && newValues.advertiser_ids[0] === allAdvertisersOption.value) {
        newValues.advertiser_ids = [];
      }
      if (newValues.brands.length === 1 && newValues.brands[0] === allBrandsOption.value) {
        newValues.brands = [];
      }
      return { ...newValues, advertiser_ids: newValues.advertiser_ids.map(String) };
    },
    [allAdvertisersOption.value, allBrandsOption.value],
  );

  const submitForm = async () => {
    try {
      // Send advertiser ids as strings to the backend
      const refinedValues = refineValues(values);
      await onSubmit(refinedValues);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const {
    values,
    errors,
    onChange,
    updateValues,
    onSubmit: handleSubmit,
  } = useForm(submitForm, initialData, () => {
    const errors = {};
    if (!values.agency_id) {
      errors.agency_id = intl.formatMessage({
        id: "ERROR_NULL_AGENCY_ID",
        defaultMessage: "Please select an agency.",
      });
    }

    // If admin agency is checked, no need to validate other fields except agency_id
    if (isAdminAgency) return errors;

    /*
     * The following validation logic only applies to the form, the server expects an empty array for admin agencies.
     * But an empty array on the frontend means that the field is yet to be filled, not that it is empty.
     * So we need to validate that at least one advertiser, seller, and brand is selected.
     * If the user selects "All Advertisers" or "All Brands", we consider the field filled and the transformation function (refineValues) will make it possible to send empty arrays to the server.
     */
    if (values.advertiser_ids.length === 0) {
      errors.advertiser_ids = intl.formatMessage({
        id: "VALIDATOR_SELECT_ADVERTISERS",
        defaultMessage: "Please select at least one advertiser.",
      });
    }

    if (values.seller_ids.length === 0) {
      errors.seller_ids = intl.formatMessage({
        id: "VALIDATOR_SELECT_SELLERS",
        defaultMessage: "Please select at least one seller.",
      });
    }

    if (is1pSeller && values.brands.length === 0) {
      errors.brands = intl.formatMessage({
        id: "VALIDATOR_SELECT_BRAND",
        defaultMessage: "Please select at least one brand.",
      });
    }
    return errors;
  });
  const is1pSeller = values.seller_ids.length === 1 && values.seller_ids[0] === "0";

  // biome-ignore lint/correctness/useExhaustiveDependencies: only run when `isAdminAgency, values.advertiser_ids.length, values.brands.length, is1pSeller` changes
  useEffect(() => {
    if (isAdminAgency) {
      setDisabledFields(["advertiser_ids", "seller_ids", "brands"]);
      updateValues({
        ...values,
        advertiser_ids: [],
        seller_ids: [],
        brands: [],
      });
    } else {
      setDisabledFields([]);
    }
    const valuesToUpdate = {};
    if (isEditMode && !isAdminAgency && values.advertiser_ids.length === 0) {
      valuesToUpdate.advertiser_ids = [allAdvertisersOption.value];
    }

    if (isEditMode && !isAdminAgency && values.brands.length === 0 && is1pSeller) {
      valuesToUpdate.brands = [allBrandsOption.value];
    }
    if (Object.keys(valuesToUpdate).length > 0) {
      updateValues({
        ...values,
        ...valuesToUpdate,
      });
    }
  }, [isAdminAgency, is1pSeller]);

  /**
   * Handle advertiser dropdown × button click.
   * If called directly, just reset advertisers.
   * @param {Event} e
   */
  const resetAgencies = (e) => {
    if (
      initialAgencies.current === null ||
      (e && !(e.target.tagName.toLowerCase() === "i" && e.target.classList.contains("clear")))
    ) {
      return;
    }

    HttpConnect.cancelRequest();

    setAgenciesData({
      ...agenciesData,
      agenciesLoading: false,
      agencies: initialAgencies.current,
    });
  };

  const fetchAgencies = (query) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery === "" && initialAgencies.current !== null) {
      return resetAgencies();
    }

    (async () => {
      setAgenciesData((prev) => ({
        ...prev,
        agenciesLoading: true,
      }));
      const response = await services.current.get("agencies").fetchAvailableAgencies(trimmedQuery);
      const formattedAgencies = response.data.map((agency) => ({
        key: agency.id,
        text: agency.title,
        value: agency.id,
      }));

      if (trimmedQuery === "" && initialAgencies.current === null) {
        initialAgencies.current = formattedAgencies;
      }

      setAgenciesData((prev) => ({
        ...prev,
        agenciesLoading: false,
        agencies: formattedAgencies,
      }));
    })();
  };

  const fetchAdvertisers = useCallback(
    async (agencyId) => {
      if (!agencyId) return;
      try {
        setAdvertisersData((prev) => ({
          ...prev,
          advertisersLoading: true,
        }));
        const response = await services.current.get("t1").advertisers(agencyId);
        setAdvertisersData((prev) => ({
          ...prev,
          advertisers: [
            allAdvertisersOption,
            ...response.data.map((advertiser) => ({
              key: advertiser.id,
              text: advertiser.title,
              value: advertiser.id,
            })),
          ],
          advertisersLoading: false,
        }));
      } catch (error) {
        console.error("Error fetching advertisers:", error);
        setAdvertisersData((prev) => ({
          ...prev,
          advertisers: [allAdvertisersOption],
          advertisersLoading: false,
        }));
      }
    },
    [allAdvertisersOption],
  );

  const fetchSellers = useCallback(
    async (data) => {
      const prevSellerQuery = sellerQuery.current ?? "";
      const query = data.searchQuery?.trim() || "";
      if (query === prevSellerQuery || query.length <= 1) return;

      sellerQuery.current = query;
      setSellersData((prev) => ({
        ...prev,
        sellersLoading: true,
      }));
      try {
        const sellers = await services.current.get("pixels").getSellers(query, { apply_permissions: false });
        let formattedSellers = [];
        if (sellers.data) {
          formattedSellers = sellers.data.map((seller) => ({
            key: seller.seller_id,
            text: seller.seller_id,
            value: seller.seller_id,
          }));
        }
        const combinedSellers = [
          ...formattedSellers,
          ...values.seller_ids
            .filter(
              (sellerId) =>
                !sellers.data.some((seller) => seller.seller_id === sellerId) && sellerId !== default1PSeller.value,
            )
            .map((sellerId) => ({
              key: sellerId,
              text: sellerId,
              value: sellerId,
            })),
        ];
        setSellersData((prev) => ({
          ...prev,
          sellers: [default1PSeller, ...combinedSellers],
          sellersLoading: false,
        }));
      } catch (error) {
        console.error("Error fetching sellers:", error);
        setSellersData((prev) => ({
          ...prev,
          sellers: [default1PSeller],
          sellersLoading: false,
        }));
      }
    },
    [values.seller_ids],
  );

  const fetchBrands = useCallback(
    async (data) => {
      // If not 1P seller, skip fetching brands
      if (!is1pSeller) return;
      const prevBrandQuery = brandQuery.current ?? "";
      const query = data.searchQuery?.trim() || "";
      if (query === prevBrandQuery) return;

      brandQuery.current = query;
      setBrandsData((prev) => ({
        ...prev,
        brandsLoading: true,
      }));
      try {
        const brands = await services.current
          .get("pixels")
          .getBrands({ brand_search_string: query, seller_id: default1PSeller.value, apply_permissions: false });
        let formattedBrands = [];
        if (brands.data) {
          formattedBrands = brands.data.map((brand) => ({
            key: brand.brand_name,
            text: brand.brand_name,
            value: brand.brand_name,
          }));
        }

        const combinedBrands = [
          ...formattedBrands,
          ...values.brands
            .filter((brandName) => !brands.data.some((brand) => brand.brand_name === brandName))
            .map((brandName) => ({
              key: brandName,
              text: brandName,
              value: brandName,
            })),
        ];
        setBrandsData((prev) => ({
          ...prev,
          brands: [allBrandsOption, ...combinedBrands],
          brandsLoading: false,
        }));
      } catch (error) {
        console.error("Error fetching brands:", error);
        setBrandsData((prev) => ({
          ...prev,
          brands: [allBrandsOption],
          brandsLoading: false,
        }));
      }
    },
    [is1pSeller, values.brands, allBrandsOption],
  );

  useEffect(() => {
    // If agency_id changes, fetch advertisers for the new agency
    if (values.agency_id && values.agency_id !== prevAgency.current) {
      fetchAdvertisers(values.agency_id);
      prevAgency.current = values.agency_id;
    }
  }, [values.agency_id, fetchAdvertisers]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Run only on initial render
  useEffect(() => {
    fetchAgencies("");
    if (isEditMode) {
      prepopulateSellersAndBrands();
    }
  }, []);

  // Helper to pre-populate sellers and brands in edit mode
  const prepopulateSellersAndBrands = useCallback(() => {
    setSellersData((prev) => ({
      ...prev,
      sellers: [
        default1PSeller,
        ...values.seller_ids
          .filter((sellerId) => sellerId !== default1PSeller.value)
          .map((sellerId) => ({ key: sellerId, text: sellerId, value: sellerId })),
      ],
    }));
    setBrandsData((prev) => ({
      ...prev,
      brands: [
        allBrandsOption,
        ...values.brands.map((brandName) => ({ key: brandName, text: brandName, value: brandName })),
      ],
    }));
  }, [values.seller_ids, values.brands, allBrandsOption]);

  const handleAgencySelect = (_e, data) => {
    const { value } = data;
    updateValues({
      ...values,
      agency_id: value,
      advertiser_ids: [],
      seller_ids: [],
      brands: [],
    });
  };

  const handleAdvertiserSelect = (_e, data) => {
    const { name, value } = data;

    if (value.includes(allAdvertisersOption.value) && value.length > 1) {
      if (values.advertiser_ids.includes(allAdvertisersOption.value)) {
        // If "All Advertisers" is already selected, remove it from the list
        const newValue = value.filter((id) => id !== allAdvertisersOption.value);
        onChange(null, { name, value: newValue });
      } else {
        // If "All Advertisers" is not selected, replace the current selection with it
        onChange(null, { name, value: [allAdvertisersOption.value] });
      }
      return;
    }

    onChange(null, { name, value });
  };

  const handleSellerSelect = (_e, data) => {
    const { name, value } = data;
    // Ensure value is always an array
    let newValue = Array.isArray(value) ? value : [value];

    // Clear brands if no sellers are selected
    if (newValue.length === 0 && name === "seller_ids") {
      updateValues({ ...values, seller_ids: [], brands: [] });
      return;
    }

    if (newValue.includes(default1PSeller.value) && newValue.length > 1) {
      if (values.seller_ids.includes(default1PSeller.value)) {
        // If 1P seller is already selected, remove it from the list
        newValue = newValue.filter((id) => id !== default1PSeller.value);
        updateValues({ ...values, seller_ids: newValue, brands: [] });
      } else {
        newValue = [default1PSeller.value];
        onChange(null, { name, value: newValue });
      }
      return;
    }
    onChange(null, { name, value: newValue });
  };

  const handleBrandSelect = (_e, data) => {
    const { name, value } = data;
    if (value.includes(allBrandsOption.value) && value.length > 1) {
      if (values.brands.includes(allBrandsOption.value)) {
        // If "All Brands" is already selected, remove it from the list
        const newValue = value.filter((id) => id !== allBrandsOption.value);
        onChange(null, { name, value: newValue });
      } else {
        // If "All Brands" is not selected, replace the current selection with it
        onChange(null, { name, value: [allBrandsOption.value] });
      }
      return;
    }
    onChange(null, { name, value });
  };

  const fetchAgenciesDebounced = useDebounce(Config.search_debounce_delay, fetchAgencies);
  const fetchSellersDebounced = useDebounce(Config.search_debounce_delay, fetchSellers);
  const fetchBrandsDebounced = useDebounce(Config.search_debounce_delay, fetchBrands);

  const isAdvertiserFieldDisabled = disabledFields.includes("advertiser_ids") || !values.agency_id;
  const isSellerFieldDisabled = disabledFields.includes("seller_ids") || !values.advertiser_ids.length;
  const isBrandFieldDisabled = disabledFields.includes("brands") || !values.seller_ids.length || !is1pSeller;

  const generateBrandPlaceholder = () => {
    if (isBrandFieldDisabled) {
      return intl.formatMessage({ id: "LABEL_ALL_BRANDS", defaultMessage: "All Brands" });
    }
    if (is1pSeller) {
      if (isEditMode && values.brands.length === 0) {
        return intl.formatMessage({ id: "PLACEHOLDER_SELECT_BRAND", defaultMessage: "Select brand(s)" });
      }
    }
    return intl.formatMessage({
      id: "PLACEHOLDER_SELECT_BRAND",
      defaultMessage: "Select brand(s)",
    });
  };

  return (
    <Segment
      basic
      style={{ padding: "0" }}
    >
      <Grid
        as={Form}
        aria-label="Product Catalog Permissions Form"
        className="product-catalog-permissions-form"
        data-testid="pcp-form"
        error={!!Object.keys(errors).length}
        noValidate
        onSubmit={handleSubmit}
        size="small"
      >
        <Grid.Column width={8}>
          <Form.Field
            disabled={isEditMode}
            error={Boolean(errors.agency_id)}
            required
          >
            <label id="agency_id">
              {intl.formatMessage({
                id: "LABEL_AGENCY",
                defaultMessage: "Agency",
              })}
            </label>
            <Dropdown
              htmlFor="agency_id"
              name="agency_id"
              data-testid="pcp-agency"
              noResultsMessage={intl.formatMessage({
                id: "EMPTY_SEARCH_RESULTS",
                defaultMessage: "No results found",
              })}
              onChange={handleAgencySelect}
              onSearchChange={(_, { searchQuery }) => {
                fetchAgenciesDebounced(searchQuery);
              }}
              options={agenciesData.agencies}
              placeholder={intl.formatMessage({
                id: "HINT_AGENCY",
                defaultMessage: "Select agency",
              })}
              required
              search
              selection
              value={values.agency_id}
            />
            <div className="custom-error">{errors?.agency_id}</div>
          </Form.Field>
          <Form.Field>
            <Checkbox
              data-testid="pcp-admin-agency"
              label={`${intl.formatMessage({
                id: "LABEL_ADMIN_AGENCY",
                defaultMessage: "Admin Agency",
              })}?`}
              onChange={(_, { checked }) => {
                setIsAdminAgency(checked);
              }}
              checked={isAdminAgency}
            />
            <small style={{ display: "block", marginTop: "0.5rem" }}>
              {intl.formatMessage({
                id: "HINT_ADMIN_AGENCY",
                defaultMessage: "If checked, this agency will have permissions to all sellers and brands.",
              })}
            </small>
          </Form.Field>
          <Form.Field
            disabled={isAdvertiserFieldDisabled}
            error={Boolean(errors.advertiser_ids)}
            required
          >
            <label>
              {intl.formatMessage({
                id: "LABEL_ADVERTISERS",
                defaultMessage: "Advertisers",
              })}
            </label>
            <Dropdown
              data-testid="pcp-advertisers"
              loading={advertisersData.advertisersLoading}
              multiple
              name="advertiser_ids"
              noResultsMessage={intl.formatMessage({
                id: "EMPTY_SEARCH_RESULTS",
                defaultMessage: "No results found",
              })}
              onChange={handleAdvertiserSelect}
              options={advertisersData.advertisers}
              placeholder={
                isAdvertiserFieldDisabled
                  ? intl.formatMessage({ id: "LABEL_ALL_ADVERTISERS", defaultMessage: "All Advertisers" })
                  : intl.formatMessage({
                      id: "HINT_ADVERTISERS",
                      defaultMessage: "Select advertisers",
                    })
              }
              required
              search
              searchQuery={isAdvertiserFieldDisabled ? "" : undefined}
              selection
              value={values.advertiser_ids}
            />
            <div className="custom-error">{errors?.advertiser_ids}</div>
          </Form.Field>
          <Form.Field
            disabled={isSellerFieldDisabled}
            error={Boolean(errors.seller_ids)}
            required
          >
            <label>
              {intl.formatMessage({
                id: "LABEL_SELLERS",
                defaultMessage: "Sellers",
              })}
            </label>
            <Dropdown
              clearable
              data-testid="pcp-sellers"
              loading={sellersData.sellersLoading}
              options={sellersData.sellers}
              multiple
              selection
              noResultsMessage={intl.formatMessage({
                id: "EMPTY_SEARCH_RESULTS",
                defaultMessage: "No results found",
              })}
              placeholder={
                isSellerFieldDisabled
                  ? intl.formatMessage({
                      id: "LABEL_ALL_SELLERS",
                      defaultMessage: "All Sellers",
                    })
                  : intl.formatMessage({
                      id: "PLACEHOLDER_SELECT_SELLER",
                      defaultMessage: "Select Seller",
                    })
              }
              name="seller_ids"
              value={values.seller_ids}
              onChange={handleSellerSelect}
              onSearchChange={(_, data) => {
                fetchSellersDebounced(data);
              }}
              search
              searchQuery={isSellerFieldDisabled ? "" : undefined}
              required
            />
            <div className="custom-error">{errors?.seller_ids}</div>
          </Form.Field>
          <Form.Field
            disabled={isBrandFieldDisabled}
            error={Boolean(errors.brands)}
            required
          >
            <label>
              {intl.formatMessage({
                id: "LABEL_BRANDS",
                defaultMessage: "Brands",
              })}
            </label>
            <Dropdown
              clearable
              data-testid="pcp-brands"
              disabled={isBrandFieldDisabled}
              loading={brandsData.brandsLoading}
              multiple
              name="brands"
              noResultsMessage={intl.formatMessage({
                id: "EMPTY_SEARCH_RESULTS",
                defaultMessage: "No results found",
              })}
              onChange={handleBrandSelect}
              onSearchChange={(_, data) => {
                fetchBrandsDebounced(data);
              }}
              options={brandsData.brands}
              placeholder={generateBrandPlaceholder()}
              required
              search
              searchQuery={isBrandFieldDisabled ? "" : undefined}
              selection
              value={values.brands}
            />
            <div className="custom-error">{errors?.brands}</div>
          </Form.Field>
        </Grid.Column>
        <Grid.Column width={16}>
          <Form.Field align="right">
            <Button
              size="tiny"
              type="button"
              onClick={onCancel}
            >
              {intl.formatMessage({
                id: "BTN_CANCEL",
                defaultMessage: "Cancel",
              })}
            </Button>
            <Button
              loading={isSubmitting}
              disabled={isSubmitting}
              size="tiny"
              color="green"
              type="submit"
              data-testid="pcp-submit"
            >
              {submitText}
            </Button>
          </Form.Field>
        </Grid.Column>
      </Grid>
    </Segment>
  );
};

export default ProductCatalogPermissionsForm;

ProductCatalogPermissionsForm.propTypes = {
  formType: PropTypes.oneOf(["create", "edit"]),
  initialData: PropTypes.object.isRequired,
  isSubmitting: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  submitText: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};
